/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import config from 'linced-app/config/environment';
import { hash } from '@linc-technologies/linced-common/utils/promise';

export default class DepositSlipsController extends Controller {
  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  toaster;

  PaymentMethod = PaymentMethod;
  @tracked
  depositSlip = null;
  @tracked
  dialogOpen = false;
  @tracked
  dataLoading = false;
  @tracked depositSlipSaving = false;
  @tracked
  discrepancies = null;
  @tracked
  saveInProgress = false;
  @tracked
  transactions = null;
  @tracked
  cancelled = null;
  @tracked
  generateDialogOpen = false;
  @tracked
  tillSessions = null;
  @tracked tillSessionsLoading = false;
  @tracked
  selectedTillSessions = [];

  @computed('tillSessionsLoading', 'selectedTillSessions.length')
  get generateDisabled() {
    return this.tillSessionsLoading || isEmpty(this.selectedTillSessions);
  }

  @action
  openGenerateDialog() {
    this.generateDialogOpen = true;
    this.tillSessions = null;
    this.tillSessionsLoading = true;
    this.selectedTillSessions = [];
    this.store
      .query('finance/till-session', {
        isClosed: true,
        isUnbanked: true,
        isTillBased: true,
      })
      .then((tillSessions) => {
        this.tillSessions = tillSessions;
        this.tillSessionsLoading = false;
      });
  }

  @action
  toggleTillSession(tillSession) {
    if (this.selectedTillSessions.includes(tillSession)) {
      this.selectedTillSessions = this.selectedTillSessions.filter(
        (item) => item !== tillSession,
      );
    } else {
      this.selectedTillSessions = [...this.selectedTillSessions, tillSession];
    }
  }

  @action
  toggleAllTillSessions() {
    if (this.selectedTillSessions.length === this.tillSessions.length) {
      // All till sessions selected; clear the selections.
      this.selectedTillSessions = [];
    } else {
      // None or some selected.  Select them all.
      this.selectedTillSessions = this.tillSessions.map((item) => item.id);
    }
  }

  @action
  closeGenerateDialog() {
    this.generateDialogOpen = false;
  }

  @action
  generateDepositSlip() {
    this.saveInProgress = true;

    let url = `${config.APP.apiHost}/finance/${config.APP.apiPath}/depositslips/:generate`;
    let data = {
      schoolId: this.session.activeSchool,
      tillSessionIds: this.selectedTillSessions.map((id) => parseInt(id, 10)),
    };
    return this.fetch
      .post(url, { data })
      .then((resp) => {
        // Push the generated deposit slip into the store, then show it.
        let depositSlip = this.store.push(
          this.store.normalize('finance/deposit-slip', resp.depositSlip),
        );
        this.viewDepositSlip(depositSlip, true);
      })
      .catch(() => {
        this.toaster.error(`Error generating Deposit Slip`);
      })
      .finally(() => {
        // Always close the dialog, so that the user must reload closed till
        // sessions before attempting again (in case it actually worked).
        this.generateDialogOpen = false;
      });
  }

  @action
  closeDialog() {
    // Revert any unsaved changes before closing dialog.
    this.depositSlip.rollbackAttributes();
    this.depositSlip = null;
    this.dataLoading = false;
    this.transactions = null;
    this.cancelled = null;
    this.discrepancies = null;
    this.dialogOpen = false;
  }

  @action
  viewDepositSlip(depositSlip, skipRefresh = false) {
    this.depositSlip = depositSlip;
    this.dataLoading = true;
    this.depositSlipSaving = false;
    this.dialogOpen = true;

    // Fetch transactions and discrepancies for this deposit slip.\
    let promises = {
      transactions: this.store.query('finance/transaction', {
        depositSlipId: depositSlip.id,
      }),
      cancelled: this.store.query('finance/transaction', {
        isCancelled: true,
        cancellationDepositSlipId: depositSlip.id,
      }),
      discrepancies: this.store.query('finance/discrepancy', {
        depositSlipId: depositSlip.id,
      }),
    };
    if (!skipRefresh) {
      // Reload in order to ensure up-to-date content.
      promises.depositSlip = depositSlip.reload();
    }
    hash(promises).then((data) => {
      this.dataLoading = false;
      this.transactions = data.transactions;
      this.cancelled = data.cancelled;
      this.discrepancies = data.discrepancies;
    });
  }
}
